import React, { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import Divider from './components/Divider';

const OTGToolbar = ({ isAdmin = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isRoutesMenuOpen, setIsRoutesMenuOpen] = useState(false);
    const [isMediaMenuOpen, setIsMediaMenuOpen] = useState(false);
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
    const routesMenuRef = useRef(null);
    const mediaMenuRef = useRef(null);
    const moreMenuRef = useRef(null);
    const adminMenuRef = useRef(null);

    // Routes & Trails dropdown menu items
    const routesSubMenu = [
        { label: "Overland Routes", href: "https://www.overlandtrailguides.com/overland-routes" },
        { label: "Plans and Pricing", href: "https://www.overlandtrailguides.com/plans-pricing" },
        { label: "Flagship Routes", href: "https://www.overlandtrailguides.com/flagship-routes" },
        { label: "Route Categories", href: "https://www.overlandtrailguides.com/route-categories" },
        { label: "Trail Rating Info", href: "https://www.overlandtrailguides.com/trail-rating-system" },
        { label: "By Region", href: "https://www.overlandtrailguides.com/overland-routes-by-region" },
        { label: "World Map", href: "https://www.overlandtrailguides.com/world-map" },
        { label: "Master Directory", href: "https://www.overlandtrailguides.com/overland-routes-master-directory" },
    ];

    // Media dropdown menu items
    const mediaSubMenu = [
        { label: "OTG YouTube", href: "https://www.overlandtrailguides.com/otg-youtube-channel" },
        { label: "OTG Podcast", href: "https://www.overlandtrailguides.com/otg-podcast" },
        { label: "Trail Tunes", href: "https://www.overlandtrailguides.com/trail-tunes" },
    ];

    // More dropdown menu items
    const moreSubMenu = [
        { label: "Terms of Use", href: "https://www.overlandtrailguides.com/terms-of-use" },
        { label: "FAQs", href: "https://community.overlandtrailguides.com/forums/index.php?forums/faqs-helpful-resources.27/" },
        { label: "Contact Support", href: "https://www.overlandtrailguides.com/contact-support" },
        { label: "Gift Card", href: "https://www.overlandtrailguides.com/gift-card" },
        { label: "Help", href: "/otgservices/event-creation-guide.html" },
    ];

    // Admin dropdown menu items
    const adminSubMenu = [
        { label: "User Stats", href: "/events/userstats" },
        { label: "Show Site Reports", href: "/events/site_reports" },
        { label: "Show Site Edits", href: "/events/audit" },
    ];

    // Toggle routes dropdown
    function toggleRoutesMenu(e) {
        e.preventDefault();
        setIsRoutesMenuOpen(!isRoutesMenuOpen);
        // Close other menus
        setIsMediaMenuOpen(false);
        setIsMoreMenuOpen(false);
        setIsAdminMenuOpen(false);
    }

    // Toggle media dropdown
    function toggleMediaMenu(e) {
        e.preventDefault();
        setIsMediaMenuOpen(!isMediaMenuOpen);
        // Close other menus
        setIsRoutesMenuOpen(false);
        setIsMoreMenuOpen(false);
        setIsAdminMenuOpen(false);
    }

    // Toggle more dropdown
    function toggleMoreMenu(e) {
        e.preventDefault();
        setIsMoreMenuOpen(!isMoreMenuOpen);
        // Close other menus
        setIsRoutesMenuOpen(false);
        setIsMediaMenuOpen(false);
        setIsAdminMenuOpen(false);
    }

    // Toggle admin dropdown
    function toggleAdminMenu(e) {
        e.preventDefault();
        setIsAdminMenuOpen(!isAdminMenuOpen);
        // Close other menus
        setIsRoutesMenuOpen(false);
        setIsMediaMenuOpen(false);
        setIsMoreMenuOpen(false);
    }

    // Function to navigate to a URL - works for both desktop and mobile
    function navigateTo(url) {
        // Using assign instead of href for more direct navigation
        window.location.assign(url);
    }

    // Define navItems AFTER the toggle functions are defined
    const navItems = [
        {
            label: "Routes & Trails",
            href: "#",
            hasDropdown: true,
            toggleFunction: toggleRoutesMenu,
            isOpen: isRoutesMenuOpen,
            menuRef: routesMenuRef,
            subMenu: routesSubMenu
        },
        { label: "Forums", href: "https://community.overlandtrailguides.com/forums/index.php" },
        {
            label: "Media",
            href: "#",
            hasDropdown: true,
            toggleFunction: toggleMediaMenu,
            isOpen: isMediaMenuOpen,
            menuRef: mediaMenuRef,
            subMenu: mediaSubMenu
        },
        { label: "Store", href: "https://www.overlandtrailguides.com/shop" },
        { label: "About Us", href: "https://www.overlandtrailguides.com/about-us" },
        ...(isAdmin ? [{
            label: "Admin",
            href: "#",
            hasDropdown: true,
            toggleFunction: toggleAdminMenu,
            isOpen: isAdminMenuOpen,
            menuRef: adminMenuRef,
            subMenu: adminSubMenu
        }] : []),
        {
            label: "More",
            href: "#",
            hasDropdown: true,
            toggleFunction: toggleMoreMenu,
            isOpen: isMoreMenuOpen,
            menuRef: moreMenuRef,
            subMenu: moreSubMenu
        },
    ];

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (routesMenuRef.current && !routesMenuRef.current.contains(event.target)) {
                setIsRoutesMenuOpen(false);
            }
            if (mediaMenuRef.current && !mediaMenuRef.current.contains(event.target)) {
                setIsMediaMenuOpen(false);
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
                setIsMoreMenuOpen(false);
            }
            if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
                setIsAdminMenuOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [routesMenuRef, mediaMenuRef, moreMenuRef, adminMenuRef]);

    return (
        <header className="bg-white shadow-md">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                {/* Logo Button */}
                <button
                    onClick={() => navigateTo("https://overlandtrailguides.com")}
                    className="flex items-center focus:outline-none"
                    title="Go to Overland Trail Guides"
                >
                    <img
                        src="/events/images/OTGBison.avif"
                        alt="OTG Logo"
                        className="h-[70px] w-[290px] object-contain"
                    />
                </button>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-6 text-gray-700 font-medium">
                    {navItems.map((item, index) => (
                        <React.Fragment key={item.label}>
                            {item.hasDropdown ? (
                                <div ref={item.menuRef} className="relative">
                                    <a
                                        href={item.href}
                                        className="flex items-center hover:text-blue-600 transition-colors"
                                        onClick={item.toggleFunction}
                                    >
                                        {item.label}
                                        <ChevronDown size={16} className="ml-1" />
                                    </a>

                                    {/* Dropdown Menu */}
                                    {item.isOpen && (
                                        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded shadow-lg py-2 w-48">
                                            {item.subMenu.map((subItem) => (
                                                <span
                                                    key={subItem.label}
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 cursor-pointer"
                                                    onClick={() => navigateTo(subItem.href)}
                                                    onTouchEnd={() => navigateTo(subItem.href)}
                                                >
                                                    {subItem.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                    onClick={() => navigateTo(item.href)}
                                    onTouchEnd={() => navigateTo(item.href)}
                                >
                                    {item.label}
                                </span>
                            )}
                            {index < navItems.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </nav>

                {/* Mobile menu button */}
                <button
                    className="md:hidden text-gray-700"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="Toggle menu"
                >
                    {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {isOpen && (
                <nav className="md:hidden bg-white shadow-inner px-4 pb-4">
                    {navItems.map((item) => (
                        item.hasDropdown ? (
                            <div key={item.label} className="py-2">
                                <button
                                    onClick={() => {
                                        if (item.label === "Routes & Trails") {
                                            setIsRoutesMenuOpen(!isRoutesMenuOpen);
                                            setIsMediaMenuOpen(false);
                                            setIsMoreMenuOpen(false);
                                            setIsAdminMenuOpen(false);
                                        } else if (item.label === "Media") {
                                            setIsMediaMenuOpen(!isMediaMenuOpen);
                                            setIsRoutesMenuOpen(false);
                                            setIsMoreMenuOpen(false);
                                            setIsAdminMenuOpen(false);
                                        } else if (item.label === "Admin") {
                                            setIsAdminMenuOpen(!isAdminMenuOpen);
                                            setIsRoutesMenuOpen(false);
                                            setIsMediaMenuOpen(false);
                                            setIsMoreMenuOpen(false);
                                        } else if (item.label === "More") {
                                            setIsMoreMenuOpen(!isMoreMenuOpen);
                                            setIsRoutesMenuOpen(false);
                                            setIsMediaMenuOpen(false);
                                            setIsAdminMenuOpen(false);
                                        }
                                    }}
                                    className="flex items-center justify-between w-full text-left text-gray-700 hover:text-blue-600"
                                >
                                    <span>{item.label}</span>
                                    <ChevronDown
                                        size={16}
                                        className={`transition-transform ${
                                            (item.label === "Routes & Trails" && isRoutesMenuOpen) ||
                                            (item.label === "Media" && isMediaMenuOpen) ||
                                            (item.label === "Admin" && isAdminMenuOpen) ||
                                            (item.label === "More" && isMoreMenuOpen)
                                                ? 'rotate-180'
                                                : ''
                                        }`}
                                    />
                                </button>

                                {((item.label === "Routes & Trails" && isRoutesMenuOpen) ||
                                    (item.label === "Media" && isMediaMenuOpen) ||
                                    (item.label === "Admin" && isAdminMenuOpen) ||
                                    (item.label === "More" && isMoreMenuOpen)) && (
                                    <div className="pl-4 mt-2 border-l-2 border-gray-100">
                                        {item.subMenu.map((subItem) => (
                                            <div
                                                key={subItem.label}
                                                className="flex items-center py-2"
                                            >
                                                {/* Plain div with onClick for mobile */}
                                                <div
                                                    className="w-full text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
                                                    onClick={() => navigateTo(subItem.href)}
                                                    onTouchEnd={() => navigateTo(subItem.href)}
                                                >
                                                    {subItem.label}
                                                </div>

                                                {/* Add a fallback direct link icon for iOS devices */}
                                                <a
                                                    href={subItem.href}
                                                    className="ml-2 text-gray-400"
                                                    aria-label={`Go to ${subItem.label}`}
                                                >
                                                    →
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                key={item.label}
                                className="block py-2 text-gray-700 border-b border-gray-100 last:border-none hover:text-blue-600 cursor-pointer"
                                onClick={() => navigateTo(item.href)}
                                onTouchEnd={() => navigateTo(item.href)}
                            >
                                {item.label}
                            </div>
                        )
                    ))}
                </nav>
            )}
        </header>
    );
};

export default OTGToolbar;